import { useEffect, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { AppShell } from "../components/AppShell";
import { OverviewTopbar } from "../components/OverviewTopbar";
import { PaywallOverlay } from "../components/PaywallOverlay";
import { IconChevronRight, IconDownload } from "../components/icons";
import {
  api,
  ApiError,
  downloadAuthenticated,
  type BillingInvoice,
  type BillingScanPackPricing,
} from "../api";
import { useAppState } from "../AppStateContext";
import { useAuth } from "../AuthContext";
import { useTour } from "../tour/tourContext";
import { LANGUAGES, isLangLocked, useI18n, useT, type Lang, type TranslationKey } from "../i18n";
import { formatMoney, localeCurrency, pickPrice } from "../money";
import { formatPlanDate, planLabel } from "../plan";
import { billingSnapshot, rememberBillingSnapshot } from "../checkoutReturn";
import { isInvoiceVisible } from "../billingHistory";
import { useBillingPlans } from "../useBillingPlans";
import { useCheckoutReturn } from "../useCheckoutReturn";
import { offerToSavePassword } from "../credentials";
import "../components/Dialog.css";
import "./overview.css";
import "./settings.css";

const CHECKOUT_OUTCOME_KEYS = {
  success: "settings.checkoutSuccess",
  processing: "settings.checkoutProcessing",
  cancelled: "settings.checkoutCancelled",
} as const satisfies Record<string, TranslationKey>;

export default function SettingsPage() {
  const { user } = useAuth();
  const { subscription, noPlan, refreshSubscription } = useAppState();
  const { t, lang, setLang } = useI18n();
  const [busyTier, setBusyTier] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const { plans } = useBillingPlans();
  const checkoutReturn = useCheckoutReturn(refreshSubscription);
  const email = user?.email ?? "";
  const displayName = user?.display_name ?? "";

  // The only way this page gets restored from the bfcache is the browser Back
  // button on the payment provider's page — nothing else navigates away from
  // the SPA. The restore revives the exact state the user left in: paywall
  // open, a button stuck on "Redirecting…". Settle everything back to idle;
  // the cancelled toast itself comes from useCheckoutReturn.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      setBusyTier(null);
      setPaywallOpen(false);
      setPickerOpen(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const checkoutPlan = async (planTier: string, provider?: string) => {
    setBusyTier(planTier);
    setMessage(null);
    setError(null);
    try {
      const successUrl = `${window.location.origin}/settings?checkout=success`;
      const cancelUrl = `${window.location.origin}/settings?checkout=cancel`;
      // `provider` decides the charge currency on the backend.
      const res = await api.checkout({
        plan_tier: planTier,
        provider,
        success_url: successUrl,
        cancel_url: cancelUrl,
      });
      if (!res.checkout_url) throw new Error(t("settings.checkoutMissing"));
      setMessage(t("settings.checkoutStarted"));
      // Record the pre-payment state so the return handler can tell an applied
      // payment from a webhook that is still in flight.
      rememberBillingSnapshot(billingSnapshot(subscription));
      window.location.assign(res.checkout_url);
    } catch (err) {
      setError(humanizeCheckoutError(err, t));
      setBusyTier(null);
    }
  };

  const activeTier = subscription?.subscription.plan_tier ?? null;
  const planName = planLabel(subscription) ?? "";
  const periodEnd = subscription?.subscription.current_period_end;
  const hasPlan = Boolean(subscription?.plan);

  return (
    <AppShell>
      <div className="overview settings anim-page">
        <OverviewTopbar />

        <p className="overview__section-label">{t("settings.sectionLabel")}</p>

        <div className="settings__grid anim-stagger" style={{ ["--anim-stagger-step" as never]: "70ms" }}>
          <div className="settings__column">
          <section>
            <h2 className="settings__heading">{t("settings.payments")}</h2>
            {checkoutReturn.outcome ? (
              <p
                className={
                  checkoutReturn.outcome === "success"
                    ? "settings__toast settings__toast--success"
                    : "settings__toast"
                }
                role="status"
              >
                {t(CHECKOUT_OUTCOME_KEYS[checkoutReturn.outcome])}
              </p>
            ) : null}
            {hasPlan ? (
              <div className="settings__scan-card">
                <div className="settings__scan-head">
                  <div>
                    <p className="settings__scan-label">{t("settings.currentPlanLabel")}</p>
                    <p className="settings__scan-sub">
                      {planName} · {t("settings.subscriptionUntil")}
                    </p>
                  </div>
                  <span className="settings__scan-badge">
                    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
                      <path d="M12 2 L14 5 L17 4 L17 8 L20 10 L17 12 L17 16 L14 15 L12 18 L10 15 L7 16 L7 12 L4 10 L7 8 L7 4 L10 5 Z" />
                      <polyline points="9 10 11 12 15 8" strokeWidth={2} />
                    </svg>
                  </span>
                </div>
                <p className="settings__scan-value">
                  {formatPlanDate(periodEnd, lang) ?? "—"}
                </p>
              </div>
            ) : noPlan ? (
              <div className="settings__no-plan">
                <p className="settings__scan-label">{t("settings.noPlanTitle")}</p>
                <p className="settings__helper">{t("settings.noPlanBody")}</p>
              </div>
            ) : (
              <p className="settings__helper">{t("common.loading")}</p>
            )}

            <button
              type="button"
              className="settings__cta"
              onClick={() => setPaywallOpen(true)}
              disabled={busyTier !== null}
            >
              {hasPlan ? t("settings.changePlanCta") : t("settings.pickPlanCta")}
              <IconChevronRight width={16} height={16} />
            </button>
            {message ? <p className="settings__toast">{message}</p> : null}
            {error ? <p className="auth-error" style={{ textAlign: "left" }}>{error}</p> : null}

            <ScanPackPurchase />

            <p className="settings__group-label">{t("settings.transactionHistoryLabel")}</p>
            {/* Remounts once a checkout settles so the new invoice shows up. */}
            <BillingInvoices key={checkoutReturn.invoicesKey} />
          </section>

          </div>

          <div className="settings__column">
          <section>
            <h2 className="settings__heading">{t("settings.profileInfo")}</h2>
            <div className="settings__form">
              <FormField label={t("settings.email")} value={email} type="email" disabled />
              <FormField label={t("settings.displayName")} value={displayName} disabled />
              <div className="settings__actions">
                <button
                  type="button"
                  className="btn--outline"
                  disabled
                  title={t("settings.profileEditDisabled")}
                >
                  {t("settings.saveChanges")}
                </button>
              </div>
            </div>
          </section>

          <section>
            <h2 className="settings__heading">{t("auth.changePasswordTitle")}</h2>
            <ChangePasswordForm />
          </section>

          <ProductTourSection />

          {isLangLocked() ? null : (
            <section>
              <h2 className="settings__heading">{t("settings.language")}</h2>
              <div className="settings__lang" role="radiogroup" aria-label={t("settings.language")}>
                {LANGUAGES.map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    role="radio"
                    aria-checked={lang === option.code}
                    className={`settings__lang-option${lang === option.code ? " settings__lang-option--active" : ""}`}
                    onClick={() => setLang(option.code as Lang)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </section>
          )}
          </div>
        </div>
      </div>
      {paywallOpen ? (
        <PaywallOverlay
          plans={plans}
          currentTier={activeTier}
          onClose={() => setPaywallOpen(false)}
          busyTier={busyTier}
          error={error}
          // A card maps to a backend plan, so check it out straight away. The
          // overlay stays open with a spinner until the redirect happens —
          // closing it first left the user staring at Settings mid-payment. If
          // the plans haven't loaded (or the backend returned fewer than we
          // show), fall back to the generic picker rather than dropping the
          // click.
          onSelect={(plan) => {
            if (!plan) {
              setPaywallOpen(false);
              setPickerOpen(true);
              return;
            }
            const price = pickPrice(plan.prices, localeCurrency(lang));
            void checkoutPlan(plan.plan_tier, price?.providers[0]);
          }}
        />
      ) : null}
      {pickerOpen ? (
        <ChangePlanDialog
          activeTier={activeTier}
          busyTier={busyTier}
          onCheckout={checkoutPlan}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </AppShell>
  );
}

/** "Product tour" entry: replays the interactive demo. Desktop only — the tour
 *  points at a layout the mobile view doesn't have. */
function ProductTourSection() {
  const { t } = useI18n();
  const { startTour } = useTour();
  const isMobile =
    typeof window !== "undefined" && window.matchMedia("(max-width: 860px)").matches;
  return (
    <section>
      <h2 className="settings__heading">{t("settings.tourHeading")}</h2>
      <p className="settings__helper">{isMobile ? t("settings.tourMobile") : t("settings.tourBody")}</p>
      <div className="settings__actions">
        <button
          type="button"
          className="btn--outline"
          onClick={startTour}
          disabled={isMobile}
          title={isMobile ? t("settings.tourMobile") : undefined}
        >
          {t("settings.tourCta")}
        </button>
      </div>
    </section>
  );
}

function humanizeCheckoutError(
  err: unknown,
  t: (k: TranslationKey, vars?: Record<string, string | number>) => string,
): string {
  if (err instanceof ApiError) {
    const msg = err.message.toLowerCase();
    const code = (err.code ?? "").toLowerCase();
    // Backend blocks billing until the email is confirmed and returns an English
    // string (that even leaks an internal API path). Show a localized hint.
    if (
      (code.includes("email") && code.includes("verif")) ||
      (msg.includes("email") && msg.includes("verif"))
    ) {
      return t("settings.checkoutEmailNotVerified");
    }
    // Backend returns 400 field=currency when the plan has no price in the
    // provider's currency.
    if (err.status === 400 && (code === "currency" || msg.includes("currency"))) {
      return t("settings.checkoutCurrencyUnavailable");
    }
  }
  // Never surface the raw backend English — fall back to a localized generic.
  return t("settings.checkoutFailed");
}

/** Buy a pack of extra scans. Price is per-currency; we charge in the locale
 *  currency (or first available) and send its provider to /checkout. Hidden
 *  when the backend exposes no pricing. */
function ScanPackPurchase() {
  const t = useT();
  const { lang } = useI18n();
  const { subscription } = useAppState();
  const [pricing, setPricing] = useState<BillingScanPackPricing | null>(null);
  const [failed, setFailed] = useState(false);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .scanPackPricing()
      .then((res) => {
        if (!cancelled) setPricing(res);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A bfcache restore via the Back button revives the pre-redirect state,
  // `busy` included — coming back means we are no longer redirecting.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setBusy(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Optional feature: stay invisible until/unless the backend offers a price.
  if (failed || !pricing) return null;
  const price = pickPrice(pricing.prices, localeCurrency(lang));
  if (!price) return null;

  const quantity = Math.max(1, Math.floor(Number.isFinite(qty) ? qty : 1));
  const provider = price.providers[0];

  const buy = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.scanPackCheckout({
        scans_count: quantity,
        provider,
        success_url: `${window.location.origin}/settings?checkout=success`,
        cancel_url: `${window.location.origin}/settings?checkout=cancel`,
      });
      if (!res.checkout_url) throw new Error(t("settings.checkoutMissing"));
      // Scan packs move `additional_scans_credit`, not the plan tier — the
      // snapshot covers both.
      rememberBillingSnapshot(billingSnapshot(subscription));
      window.location.assign(res.checkout_url);
    } catch (err) {
      setError(humanizeCheckoutError(err, t));
      setBusy(false);
    }
  };

  return (
    <div className="settings__scan-pack">
      <p className="settings__group-label">{t("settings.scanPackTitle")}</p>
      <p className="settings__helper">
        {t("settings.scanPackPerScan", { price: formatMoney(price.price, price.currency) })}
      </p>
      <div className="settings__scan-pack-row">
        <div className="settings__qty" role="group" aria-label={t("settings.scanPackQuantity")}>
          <button
            type="button"
            className="settings__qty-btn"
            onClick={() => setQty((q) => Math.max(1, (Number.isFinite(q) ? q : 1) - 1))}
            disabled={busy || quantity <= 1}
            aria-label={t("settings.scanPackDecrement")}
          >
            −
          </button>
          <span className="settings__qty-value" aria-live="polite">
            {quantity}
          </span>
          <button
            type="button"
            className="settings__qty-btn"
            onClick={() => setQty((q) => (Number.isFinite(q) ? q : 1) + 1)}
            disabled={busy}
            aria-label={t("settings.scanPackIncrement")}
          >
            +
          </button>
        </div>
        <button
          type="button"
          className="settings__cta"
          onClick={() => void buy()}
          disabled={busy}
        >
          {t("settings.scanPackBuy", {
            count: quantity,
            total: formatMoney(price.price * quantity, price.currency),
          })}
        </button>
      </div>
      {error ? <p className="auth-error" style={{ textAlign: "left" }}>{error}</p> : null}
    </div>
  );
}

function ChangePlanDialog({
  activeTier,
  busyTier,
  onCheckout,
  onClose,
}: {
  activeTier: string | null;
  busyTier: string | null;
  onCheckout: (planTier: string, provider?: string) => void;
  onClose: () => void;
}) {
  const t = useT();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Portal to <body>: the page's .anim-page transform forms the containing
  // block for `position: fixed`, which would offset this backdrop from the
  // viewport (same fix as ConfirmDialog).
  return createPortal(
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div
        className="dialog dialog--wide"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="dialog__head">
          <h2>{t("settings.choosePlanHeading")}</h2>
          <button type="button" aria-label={t("common.close")} onClick={onClose}>
            ×
          </button>
        </header>
        <div className="dialog__body">
          <PlanPicker
            activeTier={activeTier}
            busyTier={busyTier}
            onCheckout={onCheckout}
          />
          <p className="settings__helper">{t("settings.taxNote")}</p>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function PlanPicker({
  activeTier,
  busyTier,
  onCheckout,
}: {
  activeTier: string | null;
  busyTier: string | null;
  onCheckout: (planTier: string, provider?: string) => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const { plans, error, reload: load } = useBillingPlans();

  if (error) {
    return (
      <p className="auth-error" style={{ textAlign: "left" }}>
        {error}{" "}
        <button type="button" className="helper-link" onClick={load}>
          {t("common.retry")}
        </button>
      </p>
    );
  }
  if (!plans) {
    return (
      <div className="settings__plans" aria-busy="true" aria-live="polite">
        <PlanCardSkeleton />
        <PlanCardSkeleton />
        <PlanCardSkeleton />
      </div>
    );
  }
  if (plans.length === 0) {
    return <p className="helper">{t("settings.plansLoadFailed")}</p>;
  }

  return (
    <div className="settings__plans">
      {plans.map((p) => {
        const isCurrent = activeTier === p.plan_tier;
        const isBusy = busyTier === p.plan_tier;
        // Charge in the locale currency if the plan offers it, else the first
        // available price (fallback). The provider drives the currency on /checkout.
        const price = pickPrice(p.prices, localeCurrency(lang));
        const priceLabel = price ? formatMoney(price.amount, price.currency) : null;
        const provider = price?.providers[0];
        return (
          <article
            key={p.plan_tier}
            className={`settings__plan${isCurrent ? " settings__plan--current" : ""}`}
          >
            <header className="settings__plan-head">
              <p className="settings__plan-name">
                {p.display_name ?? p.plan_tier.toUpperCase()}
              </p>
              {isCurrent ? (
                <span className="settings__plan-badge">
                  {t("settings.currentPlanBadge")}
                </span>
              ) : null}
            </header>
            {/* The amount buys the whole period the card is named after —
                never a monthly rate, so no "/mo" suffix. */}
            <p className="settings__plan-price">{priceLabel ?? "—"}</p>
            <p className="settings__plan-meta">
              {p.max_scans_per_month < 0
                ? t("settings.scansUnlimited")
                : t("settings.scansPerMonth", { count: p.max_scans_per_month })}
            </p>
            {p.description ? (
              <p className="settings__plan-desc">{p.description}</p>
            ) : null}
            <button
              type="button"
              className="settings__plan-cta"
              disabled={busyTier !== null || isCurrent || !price}
              onClick={() => onCheckout(p.plan_tier, provider)}
            >
              {isBusy
                ? t("settings.choosingPlan")
                : isCurrent
                  ? t("settings.currentPlanBadge")
                  : priceLabel
                    ? t("settings.payNow", { amount: priceLabel })
                    : t("settings.choosePlan")}
            </button>
          </article>
        );
      })}
    </div>
  );
}

/** Map a backend invoice status to a badge tone + localized label. Unknown /
 * future statuses fall back to a muted badge showing the raw value, so a new
 * backend status is never silently hidden. */
function invoiceStatusBadge(
  status: string,
  t: (k: TranslationKey, vars?: Record<string, string | number>) => string,
): { tone: string; label: string } {
  switch ((status ?? "").trim().toLowerCase()) {
    case "paid":
      return { tone: "paid", label: t("settings.invoiceStatusPaid") };
    case "pending":
      return { tone: "pending", label: t("settings.invoiceStatusPending") };
    case "expired":
      return { tone: "expired", label: t("settings.invoiceStatusExpired") };
    case "failed":
      return { tone: "failed", label: t("settings.invoiceStatusFailed") };
    case "refunded":
      return { tone: "refunded", label: t("settings.invoiceStatusRefunded") };
    case "issued":
      return { tone: "muted", label: t("settings.invoiceStatusIssued") };
    default:
      return { tone: "muted", label: status || t("settings.invoiceStatusIssued") };
  }
}

/** Invoice PDFs only exist once money has changed hands (paid or refunded). */
function invoiceDownloadable(status: string): boolean {
  const s = (status ?? "").trim().toLowerCase();
  return s === "paid" || s === "refunded";
}

function BillingInvoices() {
  const t = useT();
  const { lang } = useI18n();
  const [invoices, setInvoices] = useState<BillingInvoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listBillingInvoices()
      .then((res) => {
        if (cancelled) return;
        const sorted = res.invoices
          .filter((inv) => isInvoiceVisible(inv.status))
          .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));
        setInvoices(sorted);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t("settings.invoicesLoadFailed"));
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleDownload = async (inv: BillingInvoice) => {
    setDownloading(inv.invoice_id);
    try {
      const url = inv.download_url || api.invoiceDownloadUrl(inv.invoice_id);
      await downloadAuthenticated(url, `invoice-${inv.invoice_id}.pdf`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("settings.invoicesLoadFailed"));
    } finally {
      setDownloading(null);
    }
  };

  if (error) {
    return <p className="auth-error" style={{ textAlign: "left" }}>{error}</p>;
  }

  if (!invoices) {
    return (
      <div className="settings__invoices" aria-busy="true" aria-live="polite">
        <InvoiceRowSkeleton />
        <InvoiceRowSkeleton />
      </div>
    );
  }

  if (invoices.length === 0) {
    return <div className="settings__invoices settings__invoices--empty">
      <p className="settings__helper" style={{ margin: 0 }}>{t("settings.invoicesEmpty")}</p>
    </div>;
  }

  const locale = lang === "ru" ? "ru-RU" : "en-US";
  return (
    <div className="settings__invoices">
      {invoices.map((inv, i) => {
        const dateStr = new Date(inv.created_at).toLocaleDateString(locale);
        // Charged amount is in its actual settlement currency.
        const amountStr = formatMoney(inv.amount, inv.currency);
        const isLast = i === invoices.length - 1;
        const badge = invoiceStatusBadge(inv.status, t);
        const canDownload = invoiceDownloadable(inv.status);
        const downloadTitle = canDownload
          ? t("settings.downloadInvoice")
          : t("settings.downloadUnavailable");
        return (
          <div className="settings__invoice" key={inv.invoice_id}>
            <div className="settings__invoice-info">
              <p className="settings__invoice-title">{t("settings.invoiceLabel")}</p>
              <p className="settings__invoice-date">{dateStr}</p>
            </div>
            <div className="settings__invoice-actions">
              <span className={`settings__invoice-status settings__invoice-status--${badge.tone}`}>
                {badge.label}
              </span>
              <p className="settings__invoice-amount">{amountStr}</p>
              <button
                type="button"
                className="settings__invoice-download"
                aria-label={downloadTitle}
                title={downloadTitle}
                onClick={() => void handleDownload(inv)}
                disabled={downloading === inv.invoice_id || !canDownload}
              >
                <IconDownload width={20} height={20} />
              </button>
            </div>
            {!isLast ? <span className="settings__invoice-divider" /> : null}
          </div>
        );
      })}
    </div>
  );
}

function InvoiceRowSkeleton() {
  return (
    <div className="settings__invoice" aria-hidden>
      <div className="settings__invoice-info">
        <span className="skeleton settings__invoice-skel settings__invoice-skel--title" />
        <span className="skeleton settings__invoice-skel settings__invoice-skel--date" />
      </div>
      <span className="skeleton settings__invoice-skel settings__invoice-skel--amount" />
    </div>
  );
}

function PlanCardSkeleton() {
  return (
    <article className="settings__plan settings__plan--skeleton" aria-hidden>
      <header className="settings__plan-head">
        <span className="skeleton settings__plan-skel settings__plan-skel--name" />
      </header>
      <span className="skeleton settings__plan-skel settings__plan-skel--price" />
      <span className="skeleton settings__plan-skel settings__plan-skel--meta" />
      <span className="skeleton settings__plan-skel settings__plan-skel--cta" />
    </article>
  );
}

function ChangePasswordForm() {
  const t = useT();
  const { applyAuthResponse } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (!current) {
      setError(t("auth.currentPasswordRequired"));
      return;
    }
    if (next.length < 8) {
      setError(t("auth.passwordWeak"));
      return;
    }
    setBusy(true);
    try {
      const resp = await api.changePassword({
        current_password: current,
        new_password: next,
      });
      void offerToSavePassword(resp.user.email, next);
      applyAuthResponse(resp);
      setCurrent("");
      setNext("");
      setMessage(t("auth.changePasswordSuccess"));
    } catch (err) {
      setError(humanizeChangePasswordError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="settings__form" onSubmit={onSubmit}>
      <FormField
        label={t("auth.currentPasswordPlaceholder")}
        value={current}
        onChange={setCurrent}
        type="password"
        autoComplete="current-password"
        disabled={busy}
      />
      <FormField
        label={t("auth.newPasswordPlaceholder")}
        value={next}
        onChange={setNext}
        type="password"
        autoComplete="new-password"
        disabled={busy}
      />
      {message ? <p className="settings__toast">{message}</p> : null}
      {error ? <p className="auth-error" style={{ textAlign: "left" }}>{error}</p> : null}
      <div className="settings__actions">
        <button type="submit" className="btn--outline" disabled={busy}>
          {busy ? t("auth.changePasswordSaving") : t("auth.changePasswordSubmit")}
        </button>
      </div>
    </form>
  );
}

function humanizeChangePasswordError(
  err: unknown,
  t: (k: TranslationKey, vars?: Record<string, string | number>) => string,
): string {
  if (err instanceof ApiError) {
    const lower = err.message.toLowerCase();
    if (
      err.status === 401 ||
      err.status === 403 ||
      lower.includes("invalid") ||
      lower.includes("incorrect") ||
      lower.includes("wrong")
    ) {
      return t("auth.changePasswordInvalid");
    }
  }
  return err instanceof Error ? err.message : t("auth.changePasswordFailed");
}

function FormField({
  label,
  value,
  onChange,
  type = "text",
  autoComplete,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  type?: string;
  autoComplete?: string;
  disabled?: boolean;
}) {
  return (
    <label className="settings__field">
      <span className="visually-hidden">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        autoComplete={autoComplete}
        disabled={disabled}
        placeholder={label}
      />
    </label>
  );
}
